import React from 'react';
import classNames from 'classnames';
import type { Playlist, PlaylistItem } from '@jwp/ott-common/types/playlist';
import type { AccessModel } from '@jwp/ott-common/types/config';
import { testId } from '@jwp/ott-common/src/utils/common';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';

import CardGrid from '../CardGrid/CardGrid';
import Filter from '../Filter/Filter';
import VideoDetails from '../VideoDetails/VideoDetails';
import VideoDetailsInline from '../VideoDetailsInline/VideoDetailsInline';
import VideoList from '../VideoList/VideoList';
import { useTheme } from '../../../../../ThemeContext';
import RelatedVideoShelf from '../../containers/RelatedVideoShelf/RelatedVideoShelf';

import styles from './VideoLayout.module.scss';
import { useScrollRestoration } from '@jwp/ott-ui-react/src/hooks/useScrollRestoration';

type FilterProps = {
  filterMetadata?: React.ReactNode;
  filters?: (string | { label: string; value: string })[];
  currentFilter?: string;
  defaultFilterLabel?: string;
  setFilter?: (value: string) => void;
};

type LoadMoreProps = {
  hasMore?: boolean;
  loadMore?: () => void;
};

type VideoDetailsProps = {
  title: string;
  description: string;
  image?: string;
  primaryMetadata: React.ReactNode;
  movieGenreMetadata?: string[];
  secondaryMetadata?: React.ReactNode;
  shareButton: React.ReactNode;
  favoriteButton: React.ReactNode;
  trailerButton: React.ReactNode;
  startWatchingButton: React.ReactNode;
  live?: boolean;
};

type VideoListProps = {
  relatedTitle?: string;
  onItemHover?: (item: PlaylistItem) => void;
  watchHistory?: { [key: string]: number };
  activeMediaId?: string;
  activeLabel?: string;
  getURL: (item: PlaylistItem) => string;
};

type Props = {
  inlineLayout: boolean;
  player: React.ReactNode;
  isLoading: boolean;
  accessModel: AccessModel;
  isLoggedIn: boolean;
  hasSubscription: boolean;
  children?: React.ReactNode;
  item?: PlaylistItem;
  playlist?: Playlist;
} & FilterProps &
  VideoDetailsProps &
  VideoListProps &
  LoadMoreProps;

const VideoLayout: React.FC<Props> = ({
  inlineLayout,
  player,
  playlist,
  accessModel,
  isLoading,
  isLoggedIn,
  item,
  hasSubscription,
  // video details
  title,
  description,
  image,
  primaryMetadata,
  movieGenreMetadata,
  secondaryMetadata,
  shareButton,
  favoriteButton,
  startWatchingButton,
  trailerButton,
  // list
  relatedTitle,
  watchHistory,
  activeLabel,
  // filters
  filters,
  setFilter,
  filterMetadata,
  currentFilter = '',
  defaultFilterLabel = '',
  children,
  // load more
  hasMore,
  loadMore,
  getURL,
}) => {
  const breakpoint = useBreakpoint();
  const { universalStyles } = useTheme();
  const { isRelatedVideos } = useAppConfig();

  //Scroll restoration
  const { pathname, search } = window.location;
  const path = `${pathname}${search}`;
  useScrollRestoration(path);

  const isTablet = breakpoint === Breakpoint.sm || breakpoint === Breakpoint.md;
  // only show the filters when there are two or more filters
  const hasFilters = filters && filters.length > 1;
  const showFilters = hasFilters && setFilter;

  const renderFilters = (forceDropdown: boolean) => (
    <div className={classNames(styles.filters, { [styles.filtersInline]: inlineLayout })}>
      {!!filterMetadata && inlineLayout && <span className={styles.filterMetadata}>{filterMetadata}</span>}
      {showFilters && (
        <Filter name="season" value={currentFilter} defaultLabel={defaultFilterLabel} options={filters} setValue={setFilter} forceDropdown={forceDropdown} />
      )}
    </div>
  );

  const renderRelatedVideos = (viewMode: 'grid' | 'list' | 'slider' = 'grid') => {
    if (!playlist) { return null; }

    if (viewMode === 'grid') {
      return (
        <>
          <div className={classNames(styles.relatedVideosGrid, { [styles.inlineLayout]: inlineLayout })}>
            <h2 className={styles.relatedVideosGridTitle} style={{ ...universalStyles.primaryTextColor, ...universalStyles.secondaryFontFamily }}>
              {relatedTitle || '\u00A0'}
            </h2>
            {hasFilters && <div>{renderFilters(inlineLayout)}</div>}
          </div>
          <CardGrid
            playlist={playlist}
            isLoading={isLoading}
            watchHistory={watchHistory}
            accessModel={accessModel}
            isLoggedIn={isLoggedIn}
            currentCardItem={item}
            currentCardLabel={activeLabel}
            hasSubscription={hasSubscription}
            hasMore={hasMore}
            loadMore={loadMore}
            getUrl={getURL}
          />
        </>
      );
    }

    if (viewMode === 'list') {
      return (
        <div className={styles.relatedVideosList}>
          <VideoList
            header={
              <div className={styles.relatedVideosListHeader}>
                {title && <h2 className={styles.relatedVideosListTitle}>{relatedTitle}</h2>}
                {hasFilters && renderFilters(true)}
              </div>
            }
            activeMediaId={item?.mediaid}
            activeLabel={activeLabel}
            playlist={playlist}
            watchHistory={watchHistory}
            isLoading={isLoading}
            accessModel={accessModel}
            isLoggedIn={isLoggedIn}
            hasSubscription={hasSubscription}
            hasMore={hasMore}
            loadMore={loadMore}
            getUrl={getURL}
          />
        </div>
      );
    }

    if (viewMode === 'slider') {
      return (
        <div className={styles.relatedVideosSlider}>
          <div className={classNames(styles.relatedVideosGrid, { [styles.inlineLayout]: inlineLayout })}>
            <h2 className={styles.relatedVideosGridTitle} style={{ ...universalStyles.primaryTextColor, ...universalStyles.secondaryFontFamily }}>
              {relatedTitle || '\u00A0'}
            </h2>
            {hasFilters && <div>{renderFilters(inlineLayout)}</div>}
          </div>
          <RelatedVideoShelf
            loading={isLoading}
            error={''}
            type={'playlist'}
            playlist={playlist}
            currentCardItem={item}
            currentCardLabel={activeLabel}
            watchHistory={watchHistory}
            title={title || playlist?.title}
            featured={false}
            accessModel={accessModel}
            isLoggedIn={true}
            hasSubscription={hasSubscription}
            posterAspect={'5:3'}
            visibleTilesDelta={0}
            getUrl={getURL}
            onLastItemClick={hasMore ? loadMore : null}
          />
        </div>
      );
    }

    return null;
  };

  if (inlineLayout) {
    return (
      <div className={styles.videoInlineLayout} data-testid={testId('inline-layout')}>
        <div className={styles.player}>{player}</div>
        <div className={styles.videoDetailsInline}>
          <VideoDetailsInline
            title={secondaryMetadata || title}
            live={item?.duration === 0}
            description={description}
            primaryMetadata={primaryMetadata}
            shareButton={shareButton}
            favoriteButton={isLoggedIn ? favoriteButton : null}
            trailerButton={trailerButton}
          />
        </div>
        {renderRelatedVideos(isTablet ? 'grid' : 'slider')}
        {children}
      </div>
    );
  }

  return (
    <VideoDetails
      title={title}
      description={description}
      image={image}
      startWatchingButton={startWatchingButton}
      favoriteButton={isLoggedIn ? favoriteButton : null}
      trailerButton={trailerButton}
      shareButton={shareButton}
      primaryMetadata={primaryMetadata}
      secondaryMetadata={secondaryMetadata}
      movieGenreMetadata={movieGenreMetadata}
      item={item}
    >
      {playlist && <div className={styles.relatedVideos}>{renderRelatedVideos(isTablet ? 'grid' : 'slider')}</div>}
      {children}
      {player}
    </VideoDetails>
  );
};

export default VideoLayout;
